import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Settings, BarChart3, Save, Sparkles, FlaskConical, CreditCard, AlertTriangle, KeyRound, Check, Gift, Globe, UserCog, Wrench, Copy, RefreshCw, Eye, EyeOff, ShieldCheck, Layers, Trash2 } from "lucide-react";
import DateTimePicker from "./DateTimePicker";

// Pre-fill suggestion for a new edition's timeline — offsets from today (local time).
// Editable: these are just defaults to save typing for the common case.
function suggestedEditionDates() {
  const pad = (n) => String(n).padStart(2, "0");
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  const mk = (days, h, m) => {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    d.setHours(h, m, 0, 0);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(h)}:${pad(m)}`;
  };
  return {
    team_lock_deadline: mk(14, 23, 59),
    r1_open_at: mk(15, 0, 0),
    r1_close_at: mk(25, 23, 59),
    r2_open_at: mk(28, 0, 0),
    r2_close_at: mk(38, 23, 59),
    judging_deadline: mk(42, 23, 59),
    results_announce_at: mk(44, 18, 0),
    certificate_release_at: mk(45, 0, 0),
  };
}

// ===================== Hackathon Status Panel (default export) =====================
export default function StatusPanel() {
  const { config, loadConfig, refresh } = useAuth();
  const [status, setStatus] = useState(config?.status || "upcoming");
  const [judgingComplete, setJudgingComplete] = useState(false);
  const [minJudges, setMinJudges] = useState(1);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showTeams, setShowTeams] = useState(false);
  const [dates, setDates] = useState({
    team_lock_deadline: "", r1_open_at: "", r1_close_at: "", r2_open_at: "", r2_close_at: "",
    judging_deadline: "", results_announce_at: "", certificate_release_at: "",
  });
  const [notifyPaid, setNotifyPaid] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [regClose, setRegClose] = useState("");
  const [busy, setBusy] = useState(false);
  const testMode = !!config?.test_mode;
  const toggleTestMode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/test-mode", { enabled: !testMode });
      toast.success(data.test_mode ? "Test Mode ON — gmail/personal emails accepted for the college field. Emails still send." : "Test Mode OFF — only institutional college emails accepted");
      await loadConfig();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const navigate = useNavigate();
  const impersonate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/impersonate");
      await refresh();
      toast.success("Now logged in as Test Student — explore the participant flow.");
      navigate("/team");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const maintenanceMode = !!config?.maintenance_mode;
  const [maintMsg, setMaintMsg] = useState("");
  const [maintUntil, setMaintUntil] = useState("");
  useEffect(() => {
    setMaintMsg(config?.maintenance_message || "");
    setMaintUntil(config?.maintenance_until ? toLocalInput(config.maintenance_until) : "");
  }, [config?.maintenance_message, config?.maintenance_until]);
  const toggleMaintenance = async (turnOn) => {
    setBusy(true);
    try {
      const payload = turnOn
        ? { enabled: true, message: maintMsg, until: maintUntil ? new Date(maintUntil).toISOString() : "" }
        : { enabled: false };
      const { data } = await api.post("/admin/maintenance", payload);
      toast.success(data.maintenance_mode
        ? (maintUntil ? `Maintenance ON — auto-clears at ${new Date(maintUntil).toLocaleString()}.` : "Maintenance Mode ON — visitors see the maintenance page. You keep full admin access.")
        : "Maintenance Mode OFF — the site is live again.");
      await loadConfig();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const [csHeadline, setCsHeadline] = useState("");
  const [csSubtext, setCsSubtext] = useState("");
  useEffect(() => {
    setCsHeadline(config?.coming_soon_headline || "");
    setCsSubtext(config?.coming_soon_subtext || "");
  }, [config?.coming_soon_headline, config?.coming_soon_subtext]);
  const saveComingSoon = async () => {
    setBusy(true);
    try {
      await api.post("/admin/coming-soon", { headline: csHeadline, subtext: csSubtext });
      toast.success("Coming Soon page updated.");
      await loadConfig();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const [reviewer, setReviewer] = useState(null);
  const [reviewerPw, setReviewerPw] = useState("");
  const loadReviewer = async () => {
    try { const { data } = await api.get("/admin/reviewer-account"); setReviewer(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadReviewer(); }, []);
  const saveReviewerPw = async () => {
    if (reviewerPw.trim().length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/admin/reviewer-account/set-password", { password: reviewerPw });
      toast.success("Reviewer password set — password login enabled & unlocked.");
      setReviewerPw("");
      await loadReviewer();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const toggleReviewer = async () => {
    setBusy(true);
    try {
      const next = !(reviewer?.password_login_enabled);
      const { data } = await api.post("/admin/reviewer-account/toggle", { enabled: next });
      toast.success(data.password_login_enabled
        ? "Password login ENABLED for the reviewer account."
        : "Password login DISABLED — this account now uses email OTP like everyone else.");
      await loadReviewer();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };



  // Convert ISO to <input type="datetime-local"> value (YYYY-MM-DDTHH:MM in local time)
  const toLocalInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };

  useEffect(() => {
    if (config) {
      setStatus(config.status);
      setMinJudges(config.min_judges_required ?? 1);
      setJudgingComplete(!!config.judging_complete);
      setShowParticipants(!!config.public_show_participants);
      setShowTeams(!!config.public_show_teams);
      setAutoAdvance(config.auto_advance_phase !== false);
      setRegClose(toLocalInput(config.registration_close_at));
      setDates({
        team_lock_deadline: toLocalInput(config.team_lock_deadline),
        r1_open_at: toLocalInput(config.r1_open_at),
        r1_close_at: toLocalInput(config.r1_close_at),
        r2_open_at: toLocalInput(config.r2_open_at),
        r2_close_at: toLocalInput(config.r2_close_at),
        judging_deadline: toLocalInput(config.judging_deadline),
        results_announce_at: toLocalInput(config.results_announce_at),
        certificate_release_at: toLocalInput(config.certificate_release_at),
      });
    }
  }, [config]);

  const STATUSES = [
    { v: "pre_launch", l: "Pre-Launch (Coming Soon)" },
    { v: "upcoming", l: "Upcoming (Registration Open)" }, { v: "r1_open", l: "Round 1 Open" },
    { v: "r1_review", l: "Round 1 Review" }, { v: "r2_open", l: "Round 2 Open" },
    { v: "judging", l: "Judging" }, { v: "completed", l: "Completed" },
  ];

  const saveStatus = async () => {
    setBusy(true);
    try { await api.post("/admin/hackathon-status", { status }); toast.success("Hackathon status updated"); await loadConfig(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const toggleAutoAdvance = async (next) => {
    setAutoAdvance(next);
    try {
      await api.post("/admin/auto-advance-phase", { enabled: next });
      toast.success(next ? "Phases will auto-advance from your timeline dates" : "Auto-advance off — phases are manual");
      await loadConfig();
    } catch (err) { setAutoAdvance(!next); toast.error(errorMessage(err)); }
  };
  const saveRegClose = async () => {
    setBusy(true);
    try {
      const iso = regClose ? new Date(regClose).toISOString() : null;
      await api.post("/admin/dates", { registration_close_at: iso });
      toast.success(iso ? "Registration close date set" : "Registration close date cleared — registration stays open");
      await loadConfig();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const saveMisc = async () => {
    setBusy(true);
    try { await api.post("/admin/misc-config", { judging_complete: judgingComplete, min_judges_required: parseInt(minJudges) }); toast.success("Saved"); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const savePublicStats = async () => {
    setBusy(true);
    try {
      await api.post("/admin/misc-config", { public_show_participants: showParticipants, public_show_teams: showTeams });
      toast.success("Public marketing stats updated");
      await loadConfig();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const saveDates = async () => {
    // Detect which fields are being moved FORWARD vs other edits.
    const forward = [];
    let teamLockChanged = false;
    for (const f of dateFields) {
      const newIso = dates[f.k] ? new Date(dates[f.k]).getTime() : null;
      const oldIso = config?.[f.k] ? new Date(config[f.k]).getTime() : null;
      if (newIso && oldIso && newIso > oldIso) forward.push(f.l);
      if (f.k === "team_lock_deadline" && newIso && newIso !== oldIso) teamLockChanged = true;
    }
    const willNotify = notifyPaid && teamLockChanged;
    if (forward.length > 0 || willNotify) {
      const ok = window.confirm(
        (forward.length > 0 ? `You're updating: ${forward.join(", ")}.\n\n` : "") +
        (willNotify
          ? "All PAID participants will be emailed the new Team Lock deadline (they won't be told it's an extension). "
          : "") +
        "Proceed?"
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const payload = { notify_participants: notifyPaid };
      for (const [k, v] of Object.entries(dates)) {
        if (v) payload[k] = new Date(v).toISOString();
      }
      const { data } = await api.post("/admin/dates", payload);
      toast.success(data.notify_scheduled
        ? "Dates saved · emailing paid participants the new deadline"
        : "Timeline dates updated");
      await loadConfig();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const dateFields = [
    { k: "team_lock_deadline", l: "Team Lock Deadline" },
    { k: "r1_open_at",  l: "Round 1 Opens" },
    { k: "r1_close_at", l: "Round 1 Closes" },
    { k: "r2_open_at",  l: "Round 2 Opens" },
    { k: "r2_close_at", l: "Round 2 Closes" },
    { k: "judging_deadline", l: "Judging Deadline", hint: "Judges must complete all scoring before this date. Scores lock automatically after this datetime." },
    { k: "results_announce_at", l: "Results Announcement Date", hint: "Winners go live on the public results page and announcement emails are sent automatically at this datetime." },
    { k: "certificate_release_at", l: "Certificate Release Date", hint: "All certificates become downloadable from team dashboards on this date." },
  ];

  return (
    <div className="space-y-6" data-testid="hackathon-status-panel">
      {config?.status === "pre_launch" && (
        <ConfigureAndLaunchBanner onLaunched={async () => { await loadConfig(); setStatus("upcoming"); }} />
      )}

      <EditionsManagerCard />


      <div className={`card-surface flex flex-wrap items-center justify-between gap-4 border-l-2 ${testMode ? "border-[#F59E0B]" : "border-white/10"}`} data-testid="test-mode-panel">
        <div className="flex items-start gap-3">
          <FlaskConical className={`w-5 h-5 mt-0.5 ${testMode ? "text-[#F59E0B]" : "text-gray-500"}`} strokeWidth={1.5} />
          <div>
            <div className="font-display text-xl text-white flex items-center gap-2">
              Test Mode
              <span className={`badge ${testMode ? "badge-invited" : "badge-upcoming"}`} data-testid="test-mode-state">{testMode ? "ON" : "OFF"}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">When ON: the college-email field also accepts gmail / personal domains (handy for testing with your own inbox). Emails are <b className="text-gray-300">still sent normally</b> in both modes. Turn OFF for production so only institutional emails are accepted.</p>
          </div>
        </div>
        <button onClick={toggleTestMode} disabled={busy}
          className={`!py-2 !px-4 text-xs flex items-center gap-2 ${testMode ? "btn-secondary" : "btn-primary"}`}
          data-testid="toggle-test-mode-btn">
          {busy ? <span className="spinner" /> : <FlaskConical className="w-4 h-4" />} {testMode ? "Disable Test Mode" : "Enable Test Mode"}
        </button>
      </div>

      {testMode && (
        <div className="card-surface flex flex-wrap items-center justify-between gap-4 border-l-2 border-[#0A66C2]" data-testid="impersonate-panel">
          <div className="flex items-start gap-3">
            <UserCog className="w-5 h-5 mt-0.5 text-[#5BA4F0]" strokeWidth={1.5} />
            <div>
              <div className="font-display text-xl text-white">Login as Test Student</div>
              <p className="text-xs text-gray-500 mt-1 max-w-md">Instantly become a disposable <b className="text-gray-300">Test Student</b> to walk the full participant flow (team, payment, submissions) — no OTP needed. A banner lets you return to admin anytime. Only available while Test Mode is ON.</p>
            </div>
          </div>
          <button onClick={impersonate} disabled={busy}
            className="btn-primary !py-2 !px-4 text-xs flex items-center gap-2" data-testid="impersonate-btn">
            {busy ? <span className="spinner" /> : <UserCog className="w-4 h-4" />} Login as Test Student
          </button>
        </div>
      )}

      <div className="card-surface border-l-2 border-brand/40" data-testid="coming-soon-panel">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 mt-0.5 text-brand" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="font-display text-xl text-white">Coming Soon Page</div>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">Controls the public pre-launch page (shown while status is <b className="text-gray-300">pre_launch</b>). Leave blank to use the default copy.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="label-mono block mb-1.5">Headline</label>
                <input value={csHeadline} onChange={(e) => setCsHeadline(e.target.value)} maxLength={120}
                  placeholder="Something big is about to launch."
                  className="input-field w-full" data-testid="coming-soon-headline-input" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Subtext</label>
                <textarea value={csSubtext} onChange={(e) => setCsSubtext(e.target.value)} rows={2} maxLength={300}
                  placeholder="The next IdeaThon is being prepared. Drop your email and we'll notify you the moment registration opens."
                  className="input-field w-full resize-none" data-testid="coming-soon-subtext-input" />
              </div>
              <button onClick={saveComingSoon} disabled={busy}
                className="btn-primary !py-2 !px-4 text-xs flex items-center gap-2" data-testid="coming-soon-save-btn">
                {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save Coming Soon copy
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`card-surface border-l-2 ${maintenanceMode ? "border-[#EF4444]" : "border-white/10"}`} data-testid="maintenance-panel">
        <div className="flex items-start gap-3">
          <Wrench className={`w-5 h-5 mt-0.5 ${maintenanceMode ? "text-[#EF4444]" : "text-gray-500"}`} strokeWidth={1.5} />
          <div className="flex-1">
            <div className="font-display text-xl text-white flex items-center gap-2">
              Maintenance Mode
              <span className={`badge ${maintenanceMode ? "badge-disqualified" : "badge-upcoming"}`} data-testid="maintenance-state">{maintenanceMode ? "ON" : "OFF"}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">When ON, public visitors and participants see a friendly maintenance page. You keep full admin access (login &amp; admin console stay open). Optionally set an auto-clear time and the site flips live on its own — no need to remember to switch it back.</p>

            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-mono block mb-1.5">Message to visitors (optional)</label>
                <textarea value={maintMsg} onChange={(e) => setMaintMsg(e.target.value)} rows={2} maxLength={300}
                  placeholder="e.g. Back in 30 minutes — applying updates."
                  className="input-field w-full resize-none" data-testid="maintenance-message-input" />
              </div>
              <div>
                <label className="label-mono block mb-1.5">Auto-turn off at (optional)</label>
                <input type="datetime-local" value={maintUntil} onChange={(e) => setMaintUntil(e.target.value)}
                  className="input-field w-full" data-testid="maintenance-until-input" />
                <p className="text-[11px] text-gray-600 mt-1">Leave blank to keep maintenance on until you turn it off manually.</p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              {maintenanceMode ? (
                <button onClick={() => toggleMaintenance(false)} disabled={busy}
                  className="btn-secondary !py-2 !px-4 text-xs flex items-center gap-2" data-testid="toggle-maintenance-btn">
                  {busy ? <span className="spinner" /> : <Wrench className="w-4 h-4" />} Turn Off Maintenance
                </button>
              ) : (
                <button onClick={() => toggleMaintenance(true)} disabled={busy}
                  className="btn-primary !py-2 !px-4 text-xs flex items-center gap-2" data-testid="toggle-maintenance-btn">
                  {busy ? <span className="spinner" /> : <Wrench className="w-4 h-4" />} Turn On Maintenance
                </button>
              )}
              {maintenanceMode && config?.maintenance_until && (
                <span className="text-xs text-gray-400" data-testid="maintenance-eta">Auto-clears: {new Date(config.maintenance_until).toLocaleString()}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card-surface border-l-2 border-[#5B8DEF]/50" data-testid="reviewer-account-panel">
        <div className="flex items-start gap-3">
          <KeyRound className="w-5 h-5 mt-0.5 text-[#5B8DEF]" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="font-display text-xl text-white flex items-center gap-2">
              Razorpay Reviewer Login
              {reviewer && (
                <span className={`badge ${reviewer.locked ? "badge-disqualified" : reviewer.password_login_enabled ? "badge-upcoming" : ""}`} data-testid="reviewer-state">
                  {reviewer.locked ? "LOCKED" : reviewer.password_login_enabled ? "PASSWORD ON" : reviewer.password_set ? "OTP (DISABLED)" : "NOT SET"}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              A single participant account that signs in with <b className="text-gray-300">email + password</b> (for Razorpay activation review, since their team can&apos;t receive your OTP). Email: <span className="font-mono text-gray-300">{reviewer?.email || "vijayamanikanta.pothamsetty@noobsync.com"}</span>. Locks after 3 wrong attempts — set a new password to unlock. It&apos;s temporary: <b className="text-gray-300">turn it off anytime</b> and the account reverts to normal email-OTP login.
              {reviewer?.locked && <span className="text-red-400"> Currently locked ({reviewer.failed_attempts} failed attempts).</span>}
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]">
                <label className="label-mono block mb-1.5">{reviewer?.password_set ? "New password" : "Set password"} (min 8 chars)</label>
                <input type="password" value={reviewerPw} onChange={(e) => setReviewerPw(e.target.value)}
                  placeholder="••••••••" className="input-field w-full" data-testid="reviewer-password-input" />
              </div>
              <button onClick={saveReviewerPw} disabled={busy}
                className="btn-primary !py-2.5 !px-4 text-xs flex items-center gap-2" data-testid="reviewer-set-password-btn">
                {busy ? <span className="spinner" /> : <KeyRound className="w-4 h-4" />} {reviewer?.locked ? "Reset & Unlock" : reviewer?.password_set ? "Update Password" : "Set Password"}
              </button>
              {reviewer?.password_set && (
                <button onClick={toggleReviewer} disabled={busy}
                  className={`!py-2.5 !px-4 text-xs flex items-center gap-2 ${reviewer.password_login_enabled ? "btn-secondary" : "btn-primary"}`}
                  data-testid="reviewer-toggle-btn">
                  {busy ? <span className="spinner" /> : <KeyRound className="w-4 h-4" />} {reviewer.password_login_enabled ? "Switch to OTP" : "Enable Password Login"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <PaymentGatewayCard />

      <ZohoGatewayCard />

      <AmbassadorCard />
      <MetaPixelCard />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface">
          <div className="flex items-center gap-2 mb-3"><Settings className="w-4 h-4 text-brand" /><div className="font-display text-xl text-white">Hackathon Status</div></div>
          <p className="text-xs text-gray-500 mb-4">Controls visibility of submission rounds and the results page.</p>
          <select value={status} onChange={e => setStatus(e.target.value)} className="input-field" data-testid="hackathon-status-select">
            {STATUSES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <button onClick={saveStatus} disabled={busy} className="btn-primary w-full mt-3 flex items-center justify-center gap-2" data-testid="save-status-btn">
            {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Update Status
          </button>

          <div className="mt-4 pt-4 border-t border-white/10">
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="auto-advance-label">
              <input type="checkbox" checked={autoAdvance} onChange={e => toggleAutoAdvance(e.target.checked)}
                className="mt-0.5 accent-[#FF6B2B] w-4 h-4" data-testid="auto-advance-toggle" />
              <span className="text-sm text-gray-300">
                Auto-advance rounds from the timeline dates
                <span className="block text-[11px] text-gray-500 mt-0.5 leading-snug">Round 1/2 open &amp; close automatically as each date passes (Round 1 Opens → Round 1 Open, etc.). Registration and “Completed” are never changed automatically.</span>
              </span>
            </label>
          </div>

          <div className="mt-4 pt-4 border-t border-white/10">
            <label className="label-mono block mb-1">Registration Closes <span className="text-gray-600 normal-case">(optional)</span></label>
            <p className="text-[11px] text-gray-500 mb-2 leading-snug">New sign-ups stop after this time (already-registered users can still pay, form teams &amp; submit). Leave blank to keep registration open until you end the edition.</p>
            <input type="datetime-local" value={regClose} onChange={e => setRegClose(e.target.value)}
              className="input-field" data-testid="reg-close-input" />
            <div className="flex gap-2 mt-2">
              <button onClick={saveRegClose} disabled={busy} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-reg-close-btn">
                {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save
              </button>
              {regClose && (
                <button onClick={() => { setRegClose(""); }} disabled={busy} className="btn-secondary" data-testid="clear-reg-close-btn">Clear</button>
              )}
            </div>
          </div>
        </div>

        <div className="card-surface">
          <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-brand" /><div className="font-display text-xl text-white">Judging Config</div></div>
          <div className="space-y-4">
            <div>
              <label className="label-mono block mb-1">Min Judges Required</label>
              <input type="number" min="1" max="50" value={minJudges} onChange={e => setMinJudges(e.target.value)} className="input-field" data-testid="min-judges-input" />
            </div>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={judgingComplete} onChange={e => setJudgingComplete(e.target.checked)} className="accent-[#FF6B2B]" data-testid="judging-complete-toggle" />
              <span className="text-sm text-gray-300">Reveal results page publicly (judging_complete)</span>
            </label>
            <button onClick={saveMisc} disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="save-misc-btn">
              {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>

        <div className="card-surface" data-testid="public-marketing-stats-panel">
          <div className="flex items-center gap-2 mb-1"><Globe className="w-4 h-4 text-brand" /><div className="font-display text-xl text-white">Public Marketing Stats</div></div>
          <p className="text-xs text-gray-500 mb-4">Choose which live counts appear publicly on the landing page. Both are hidden by default — the raw registration count is never exposed unless you turn it on here.</p>
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={showParticipants} onChange={e => setShowParticipants(e.target.checked)} className="accent-[#FF6B2B]" data-testid="show-participants-toggle" />
              <span className="text-sm text-gray-300">Show total participants publicly</span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={showTeams} onChange={e => setShowTeams(e.target.checked)} className="accent-[#FF6B2B]" data-testid="show-teams-toggle" />
              <span className="text-sm text-gray-300">Show total teams publicly</span>
            </label>
            <button onClick={savePublicStats} disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="save-public-stats-btn">
              {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      </div>

      <div className="card-surface" data-testid="timeline-dates-panel">
        <div className="flex items-center gap-2 mb-1"><Settings className="w-4 h-4 text-brand" /><div className="font-display text-xl text-white">Timeline Dates</div></div>
        <p className="text-xs text-gray-500 mb-5">Set the actual dates students will see across the landing page, dashboards, and emails.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {dateFields.map(f => (
            <div key={f.k}>
              <label className="label-mono block mb-1">{f.l}</label>
              <input type="datetime-local" value={dates[f.k]} onChange={e => setDates(d => ({ ...d, [f.k]: e.target.value }))}
                className="input-field" data-testid={`date-${f.k}`} />
              {f.hint && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{f.hint}</p>}
            </div>
          ))}
        </div>
        <label className="flex items-start gap-2.5 mt-5 cursor-pointer select-none" data-testid="notify-paid-label">
          <input type="checkbox" checked={notifyPaid} onChange={(e) => setNotifyPaid(e.target.checked)}
            className="mt-0.5 accent-brand w-4 h-4" data-testid="notify-paid-checkbox" />
          <span className="text-sm text-gray-300">
            Email paid participants the new <span className="text-white">Team Lock deadline</span>
            <span className="block text-[11px] text-gray-500 mt-0.5 leading-snug">Sends a neutral “team registration closes on …” note (no mention of an extension) to everyone who has paid. Only fires when the Team Lock deadline actually changes.</span>
          </span>
        </label>
        <button onClick={saveDates} disabled={busy} className="btn-primary mt-4 flex items-center gap-2" data-testid="save-dates-btn">
          {busy ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save All Dates
        </button>
      </div>

      <LaunchEditionPanel />
    </div>
  );
}

// ===================== Launch a New Edition =====================
function LaunchEditionPanel() {
  const { loadConfig } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "IdeaThon", edition: "", theme: "", tagline: "",
    entry_fee: 200, min_team_size: 2, max_team_size: 6,
    team_lock_deadline: "", r1_open_at: "", r1_close_at: "", r2_open_at: "", r2_close_at: "",
    judging_deadline: "", results_announce_at: "", certificate_release_at: "",
    prize_1_base: 15000, prize_2_base: 10000, prize_3_base: 5000,
    prize_1_pool_percent: 15, prize_2_pool_percent: 10, prize_3_pool_percent: 10,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.edition || !form.team_lock_deadline || !form.r1_open_at || !form.r1_close_at || !form.r2_open_at || !form.r2_close_at)
      return toast.error("Edition tag and all four round dates are required");
    if (!window.confirm("This archives the current active edition (sets it to 'completed') and starts a fresh one with zero teams. Continue?")) return;
    setBusy(true);
    try {
      const payload = { ...form };
      ["team_lock_deadline","r1_open_at","r1_close_at","r2_open_at","r2_close_at"].forEach(k => {
        payload[k] = new Date(form[k]).toISOString();
      });
      // Optional results-phase dates — only send when set
      ["judging_deadline","results_announce_at","certificate_release_at"].forEach(k => {
        payload[k] = form[k] ? new Date(form[k]).toISOString() : null;
      });
      ["entry_fee","min_team_size","max_team_size","prize_1_base","prize_2_base","prize_3_base"].forEach(k => payload[k] = parseInt(form[k]) || 0);
      ["prize_1_pool_percent","prize_2_pool_percent","prize_3_pool_percent"].forEach(k => payload[k] = parseFloat(form[k]) || 0);
      await api.post("/admin/launch-edition", payload);
      toast.success("New edition launched");
      await loadConfig();
      setOpen(false);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className="card-surface border-l-2 border-brand" data-testid="launch-edition-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-brand" /><div className="font-display text-xl text-white">Launch a new edition</div></div>
          <p className="text-xs text-gray-500 max-w-xl">Wraps up the current edition (status=completed, hidden from /register) and seeds a fresh one with its own dates, theme, prize formula, and team rules. Use this between hackathons in the series — e.g. *IdeaThon · Sustainability Edition · 2027*.</p>
        </div>
        <button onClick={() => { setForm(f => ({ ...f, ...suggestedEditionDates() })); setOpen(true); }} className="btn-primary flex items-center gap-2" data-testid="open-launch-edition">
          <Sparkles className="w-4 h-4" /> Launch New Edition
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-5 overflow-y-auto" data-testid="launch-edition-modal">
          <div className="card-surface max-w-2xl w-full my-10">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl text-white">Launch new edition</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-sm">Cancel</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Inp l="Name" v={form.name} on={(v) => set("name", v)} testid="le-name" />
              <Inp l="Edition tag (e.g. 2027 Spring)" v={form.edition} on={(v) => set("edition", v)} testid="le-edition" />
              <ThemeSelect value={form.theme} onChange={(v) => set("theme", v)} testid="le-theme" />
              <Inp l="Tagline (optional)" v={form.tagline} on={(v) => set("tagline", v)} testid="le-tagline" />
              <Inp l="Entry Fee ₹" v={form.entry_fee} on={(v) => set("entry_fee", v)} type="number" />
              <Inp l="Min Team Size" v={form.min_team_size} on={(v) => set("min_team_size", v)} type="number" />
              <Inp l="Max Team Size" v={form.max_team_size} on={(v) => set("max_team_size", v)} type="number" />
              <div />
              <DateTimePicker label="Team Lock Deadline" value={form.team_lock_deadline} onChange={(v) => set("team_lock_deadline", v)} testid="le-team-lock" />
              <DateTimePicker label="Round 1 Opens" value={form.r1_open_at} onChange={(v) => set("r1_open_at", v)} testid="le-r1-open" />
              <DateTimePicker label="Round 1 Closes" value={form.r1_close_at} onChange={(v) => set("r1_close_at", v)} testid="le-r1-close" />
              <DateTimePicker label="Round 2 Opens" value={form.r2_open_at} onChange={(v) => set("r2_open_at", v)} testid="le-r2-open" />
              <DateTimePicker label="Round 2 Closes / Finale" value={form.r2_close_at} onChange={(v) => set("r2_close_at", v)} testid="le-r2-close" />
              <div />
              <DateTimePicker label="Judging Deadline" value={form.judging_deadline} onChange={(v) => set("judging_deadline", v)} testid="le-judging-deadline" optional hint="Judges must finish scoring before this. Scores lock automatically after." />
              <DateTimePicker label="Results Announcement Date" value={form.results_announce_at} onChange={(v) => set("results_announce_at", v)} testid="le-results-announce" optional hint="Winners go live on the public results page on this date." />
              <DateTimePicker label="Certificate Release Date" value={form.certificate_release_at} onChange={(v) => set("certificate_release_at", v)} testid="le-cert-release" optional hint="Certificates become downloadable from team dashboards on this date." />
              <div />
              <Inp l="Prize 1 base ₹" v={form.prize_1_base} on={(v) => set("prize_1_base", v)} type="number" />
              <Inp l="Prize 1 pool %" v={form.prize_1_pool_percent} on={(v) => set("prize_1_pool_percent", v)} type="number" />
              <Inp l="Prize 2 base ₹" v={form.prize_2_base} on={(v) => set("prize_2_base", v)} type="number" />
              <Inp l="Prize 2 pool %" v={form.prize_2_pool_percent} on={(v) => set("prize_2_pool_percent", v)} type="number" />
              <Inp l="Prize 3 base ₹" v={form.prize_3_base} on={(v) => set("prize_3_base", v)} type="number" />
              <Inp l="Prize 3 pool %" v={form.prize_3_pool_percent} on={(v) => set("prize_3_pool_percent", v)} type="number" />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="confirm-launch-edition">
                {busy ? <span className="spinner" /> : <Sparkles className="w-4 h-4" />} Archive current + Launch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== Theme Select (dropdown + Other free text) =====================
const THEME_OPTIONS = [
  "General / Open Innovation",
  "Artificial Intelligence & Agents",
  "Generative AI & LLMs",
  "Sustainability & Climate Tech",
  "Fintech & Payments",
  "HealthTech & Wellness",
  "EdTech & Future of Learning",
  "Web3, Blockchain & Crypto",
  "Cybersecurity & Privacy",
  "Smart Cities & Mobility",
  "AgriTech & Food Security",
  "AR/VR & Spatial Computing",
  "Developer Tools & Open Source",
  "Social Impact & Accessibility",
  "Hardware & IoT",
  "Other",
];

function ThemeSelect({ value, onChange, testid = "cfg-theme" }) {
  const isCanonical = (v) => !v || THEME_OPTIONS.slice(0, -1).includes(v);
  const [picked, setPicked] = useState(isCanonical(value) ? (value || "") : "Other");
  const [custom, setCustom] = useState(isCanonical(value) ? "" : (value || ""));

  useEffect(() => {
    if (isCanonical(value)) { setPicked(value || ""); setCustom(""); }
    else { setPicked("Other"); setCustom(value || ""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onPick = (v) => {
    setPicked(v);
    if (v === "Other") onChange(custom);
    else onChange(v);
  };
  const onCustom = (v) => {
    setCustom(v);
    onChange(v);
  };

  return (
    <div data-testid={testid}>
      <label className="label-mono block mb-2 flex items-center gap-2">
        Theme
        <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-white/10 text-gray-400 rounded-sm">Optional</span>
      </label>
      <select
        value={picked}
        onChange={(e) => onPick(e.target.value)}
        className="input-field"
        data-testid={`${testid}-select`}
      >
        <option value="">No theme — leave blank</option>
        {THEME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      {picked === "Other" && (
        <input
          type="text"
          value={custom}
          onChange={(e) => onCustom(e.target.value)}
          placeholder="Enter your custom theme"
          className="input-field mt-2"
          data-testid={`${testid}-custom`}
        />
      )}
    </div>
  );
}

const Inp = ({ l, v, on, type = "text", testid, hint }) => (
  <div>
    <label className="label-mono block mb-1">{l}</label>
    <input type={type} value={v} onChange={e => on(e.target.value)} className="input-field" data-testid={testid} />
    {hint && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{hint}</p>}
  </div>
);

// ===================== Configure & Launch Banner =====================
function ConfigureAndLaunchBanner({ onLaunched }) {
  const { config } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const toLocalInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };

  const [form, setForm] = useState({
    name: "IdeaThon", edition: "2026", theme: "", tagline: "",
    entry_fee: 200, min_team_size: 2, max_team_size: 6, min_judges_required: 1,
    team_lock_deadline: "", r1_open_at: "", r1_close_at: "", r2_open_at: "", r2_close_at: "",
    judging_deadline: "", results_announce_at: "", certificate_release_at: "",
    prize_1_base: 15000, prize_2_base: 10000, prize_3_base: 5000,
    prize_1_pool_percent: 15, prize_2_pool_percent: 10, prize_3_pool_percent: 5,
  });

  useEffect(() => {
    if (!config) return;
    setForm(f => ({
      ...f,
      name: config.name ?? f.name,
      edition: config.edition ?? f.edition,
      theme: config.theme ?? "",
      tagline: config.tagline ?? "",
      entry_fee: config.entry_fee ?? f.entry_fee,
      min_team_size: config.min_team_size ?? f.min_team_size,
      max_team_size: config.max_team_size ?? f.max_team_size,
      min_judges_required: config.min_judges_required ?? f.min_judges_required,
      team_lock_deadline: toLocalInput(config.team_lock_deadline),
      r1_open_at: toLocalInput(config.r1_open_at),
      r1_close_at: toLocalInput(config.r1_close_at),
      r2_open_at: toLocalInput(config.r2_open_at),
      r2_close_at: toLocalInput(config.r2_close_at),
      judging_deadline: toLocalInput(config.judging_deadline),
      results_announce_at: toLocalInput(config.results_announce_at),
      certificate_release_at: toLocalInput(config.certificate_release_at),
      prize_1_base: config.prize_1_base ?? f.prize_1_base,
      prize_2_base: config.prize_2_base ?? f.prize_2_base,
      prize_3_base: config.prize_3_base ?? f.prize_3_base,
      prize_1_pool_percent: config.prize_1_pool_percent ?? f.prize_1_pool_percent,
      prize_2_pool_percent: config.prize_2_pool_percent ?? f.prize_2_pool_percent,
      prize_3_pool_percent: config.prize_3_pool_percent ?? f.prize_3_pool_percent,
    }));
  }, [config]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (goLive) => {
    const missingDates = ["team_lock_deadline","r1_open_at","r1_close_at","r2_open_at","r2_close_at"]
      .filter(k => !form[k]);
    if (missingDates.length) return toast.error(`Set all 5 dates first (missing: ${missingDates.join(", ")})`);
    if (parseInt(form.min_team_size) > parseInt(form.max_team_size))
      return toast.error("Min team size cannot exceed max");

    if (goLive && !window.confirm("Save this config AND go live now? Registration opens immediately and the landing page switches to the full hackathon view.")) return;

    setBusy(true);
    try {
      const payload = { ...form, go_live: goLive };
      ["team_lock_deadline","r1_open_at","r1_close_at","r2_open_at","r2_close_at"].forEach(k => {
        payload[k] = new Date(form[k]).toISOString();
      });
      ["judging_deadline","results_announce_at","certificate_release_at"].forEach(k => {
        payload[k] = form[k] ? new Date(form[k]).toISOString() : null;
      });
      ["entry_fee","min_team_size","max_team_size","min_judges_required","prize_1_base","prize_2_base","prize_3_base"].forEach(k => payload[k] = parseInt(form[k]) || 0);
      ["prize_1_pool_percent","prize_2_pool_percent","prize_3_pool_percent"].forEach(k => payload[k] = parseFloat(form[k]) || 0);

      await api.post("/admin/configure-and-launch", payload);
      toast.success(goLive ? "Edition is live. Registration is open." : "Config saved. Click Go Live when ready.");
      setOpen(false);
      if (goLive && onLaunched) await onLaunched();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="card-surface border-l-2 border-brand flex flex-wrap items-center justify-between gap-4" data-testid="go-live-banner">
        <div>
          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-brand" /><div className="font-display text-xl text-white">Edition is in Pre-Launch</div></div>
          <p className="text-xs text-gray-500 max-w-xl">Public landing shows "Coming Soon", registration is blocked. Configure all dates, team rules, and prizes — then click Go Live and the full hackathon goes public instantly.</p>
        </div>
        <button onClick={() => { const sug = suggestedEditionDates(); setForm(f => { const next = { ...f }; Object.keys(sug).forEach(k => { if (!next[k]) next[k] = sug[k]; }); return next; }); setOpen(true); }} className="btn-primary flex items-center gap-2" data-testid="go-live-btn">
          <Sparkles className="w-4 h-4" /> Configure &amp; Launch
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-5 overflow-y-auto" data-testid="configure-launch-modal">
          <div className="card-surface max-w-3xl w-full my-10">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-2xl text-white">Configure &amp; Launch</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-sm" data-testid="close-launch-modal">Cancel</button>
            </div>
            <p className="text-xs text-gray-500 mb-6">Every field below applies to the <span className="text-white">current</span> edition. Save without launching if you want to keep iterating, or click "Save &amp; Go Live" to flip the public site on right now.</p>

            <div className="space-y-6">
              <div>
                <div className="label-mono mb-2">Identity</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Inp l="Hackathon Name" v={form.name} on={(v) => set("name", v)} testid="cfg-name" />
                  <Inp l="Edition Tag (e.g. 2026 Spring)" v={form.edition} on={(v) => set("edition", v)} testid="cfg-edition" />
                  <ThemeSelect value={form.theme} onChange={(v) => set("theme", v)} testid="cfg-theme" />
                  <Inp l="Tagline (optional)" v={form.tagline} on={(v) => set("tagline", v)} testid="cfg-tagline" />
                </div>
              </div>

              <div>
                <div className="label-mono mb-2">Team Rules &amp; Pricing</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Inp l="Entry Fee ₹ (per person)" v={form.entry_fee} on={(v) => set("entry_fee", v)} type="number" testid="cfg-entry-fee" />
                  <Inp l="Min Judges per Team" v={form.min_judges_required} on={(v) => set("min_judges_required", v)} type="number" testid="cfg-min-judges" />
                  <Inp l="Min Team Size" v={form.min_team_size} on={(v) => set("min_team_size", v)} type="number" testid="cfg-min-team" />
                  <Inp l="Max Team Size" v={form.max_team_size} on={(v) => set("max_team_size", v)} type="number" testid="cfg-max-team" />
                </div>
              </div>

              <div>
                <div className="label-mono mb-2">Timeline</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Inp l="Team Lock Deadline" v={form.team_lock_deadline} on={(v) => set("team_lock_deadline", v)} type="datetime-local" testid="cfg-team-lock" />
                  <Inp l="Round 1 Opens" v={form.r1_open_at} on={(v) => set("r1_open_at", v)} type="datetime-local" testid="cfg-r1-open" />
                  <Inp l="Round 1 Closes" v={form.r1_close_at} on={(v) => set("r1_close_at", v)} type="datetime-local" testid="cfg-r1-close" />
                  <Inp l="Round 2 Opens" v={form.r2_open_at} on={(v) => set("r2_open_at", v)} type="datetime-local" testid="cfg-r2-open" />
                  <Inp l="Round 2 Closes / Finale" v={form.r2_close_at} on={(v) => set("r2_close_at", v)} type="datetime-local" testid="cfg-r2-close" />
                  <Inp l="Judging Deadline" v={form.judging_deadline} on={(v) => set("judging_deadline", v)} type="datetime-local" testid="cfg-judging-deadline" hint="Judges must complete all scoring before this date. Scores lock automatically after this datetime." />
                  <Inp l="Results Announcement Date" v={form.results_announce_at} on={(v) => set("results_announce_at", v)} type="datetime-local" testid="cfg-results-announce" hint="Winners go live on the public results page and announcement emails are sent automatically at this datetime." />
                  <Inp l="Certificate Release Date" v={form.certificate_release_at} on={(v) => set("certificate_release_at", v)} type="datetime-local" testid="cfg-certificate-release" hint="All certificates become downloadable from team dashboards on this date." />
                </div>
              </div>

              <div>
                <div className="label-mono mb-2">Prize Formula</div>
                <p className="text-xs text-gray-500 mb-3 max-w-xl">Each prize = base ₹ + % of the live prize pool. The pool grows dynamically with paid registrations.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Inp l="🥇 Rank 1 base ₹" v={form.prize_1_base} on={(v) => set("prize_1_base", v)} type="number" />
                  <Inp l="🥇 Rank 1 pool %" v={form.prize_1_pool_percent} on={(v) => set("prize_1_pool_percent", v)} type="number" />
                  <Inp l="🥈 Rank 2 base ₹" v={form.prize_2_base} on={(v) => set("prize_2_base", v)} type="number" />
                  <Inp l="🥈 Rank 2 pool %" v={form.prize_2_pool_percent} on={(v) => set("prize_2_pool_percent", v)} type="number" />
                  <Inp l="🥉 Rank 3 base ₹" v={form.prize_3_base} on={(v) => set("prize_3_base", v)} type="number" />
                  <Inp l="🥉 Rank 3 pool %" v={form.prize_3_pool_percent} on={(v) => set("prize_3_pool_percent", v)} type="number" />
                </div>
              </div>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-secondary" data-testid="cancel-launch-modal">Cancel</button>
              <button onClick={() => submit(false)} disabled={busy} className="btn-secondary flex items-center justify-center gap-2" data-testid="save-config-only">
                Save (stay in Pre-Launch)
              </button>
              <button onClick={() => submit(true)} disabled={busy} className="btn-primary flex items-center justify-center gap-2" data-testid="save-and-go-live">
                {busy ? <span className="spinner" /> : <Sparkles className="w-4 h-4" />} Save &amp; Go Live
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ===================== Payment Gateway (Live ⇄ Test) =====================
function PaymentGatewayCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmTest, setConfirmTest] = useState(false);
  const [confirmClean, setConfirmClean] = useState(false);
  const [showKeys, setShowKeys] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/payment-mode"); setInfo(data); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setMode = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payment-mode", { mode });
      toast.success(data.payment_mode === "test" ? "Gateway switched to TEST — no real money is collected" : "Gateway switched to LIVE — real payments enabled");
      setConfirmTest(false);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const cleanTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clean-test-payments");
      toast.success(`Cleaned ${data.cleaned} test transaction(s) — those users are now unpaid`);
      setConfirmClean(false);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  if (!info) return null;
  const isTest = info.payment_mode === "test";

  return (
    <div className={`card-surface border-l-2 ${isTest ? "border-[#F59E0B]" : "border-emerald-500"}`} data-testid="payment-gateway-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <CreditCard className={`w-5 h-5 mt-0.5 ${isTest ? "text-[#F59E0B]" : "text-emerald-400"}`} strokeWidth={1.5} />
          <div>
            <div className="font-display text-xl text-white flex items-center gap-2">
              Payment Gateway
              <span className={`badge ${isTest ? "badge-invited" : "badge-accepted"}`} data-testid="payment-mode-state">{isTest ? "TEST" : "LIVE"}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-lg">
              Emergency switch for the Razorpay gateway. In <b className="text-gray-300">TEST</b> mode only admins can transact (regular users see a maintenance notice), and every payment is tagged as test &amp; excluded from revenue.
            </p>
            {!info.live_keys_configured && (
              <p className="text-[11px] text-amber-400 mt-2 flex items-center gap-1.5" data-testid="live-keys-warning">
                <AlertTriangle className="w-3.5 h-3.5" /> Live keys not configured yet — LIVE mode will not process real payments until they're set.
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {isTest ? (
            <button onClick={() => setMode("live")} disabled={busy} className="btn-primary !py-2 !px-4 text-xs flex items-center gap-2" data-testid="switch-to-live-btn">
              {busy ? <span className="spinner" /> : <CreditCard className="w-4 h-4" />} Switch to LIVE
            </button>
          ) : (
            <button onClick={() => setConfirmTest(true)} disabled={busy} className="btn-secondary !py-2 !px-4 text-xs flex items-center gap-2" data-testid="switch-to-test-btn">
              <FlaskConical className="w-4 h-4" /> Switch to TEST
            </button>
          )}
          <button onClick={() => setConfirmClean(true)} disabled={busy || info.test_paid_count === 0}
            className="text-[11px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#EF4444] disabled:opacity-40 disabled:hover:text-gray-400"
            data-testid="clean-test-payments-btn">
            Clean test transactions ({info.test_paid_count})
          </button>
        </div>
      </div>

      <button onClick={() => setShowKeys((s) => !s)} className="mt-4 text-xs font-mono uppercase tracking-widest text-gray-400 hover:text-brand flex items-center gap-1.5" data-testid="toggle-keys-config">
        <KeyRound className="w-3.5 h-3.5" /> {showKeys ? "Hide gateway keys" : "Configure gateway keys"}
      </button>
      {showKeys && <RazorpayKeysConfig onChange={load} />}

      {confirmTest && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-5" data-testid="confirm-test-modal">
          <div className="card-surface max-w-md w-full border-l-2 border-[#F59E0B]">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-[#F59E0B]" /><h3 className="font-display text-xl text-white">Switch LIVE gateway to TEST?</h3></div>
            <p className="text-sm text-gray-400">No real money will be collected while in TEST mode. Regular users will see a maintenance notice and cannot pay — only admins can transact. Remember to switch back to LIVE and run “Clean test transactions” afterwards.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmTest(false)} className="btn-secondary flex-1" data-testid="cancel-test-switch">Cancel</button>
              <button onClick={() => setMode("test")} disabled={busy} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="confirm-test-switch">
                {busy ? <span className="spinner" /> : <FlaskConical className="w-4 h-4" />} Yes, switch to TEST
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmClean && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-5" data-testid="confirm-clean-modal">
          <div className="card-surface max-w-md w-full border-l-2 border-[#EF4444]">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-[#EF4444]" /><h3 className="font-display text-xl text-white">Clean test transactions?</h3></div>
            <p className="text-sm text-gray-400">This reverts <b className="text-white">{info.test_paid_count}</b> test-paid registration(s) back to <b className="text-white">unpaid</b> (revoking their access) and deletes their invoices. This cannot be undone.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmClean(false)} className="btn-secondary flex-1" data-testid="cancel-clean">Cancel</button>
              <button onClick={cleanTest} disabled={busy} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="confirm-clean">
                {busy ? <span className="spinner" /> : null} Yes, clean now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== Razorpay key configuration (encrypted at rest) =====================
function RazorpayKeysConfig({ onChange }) {
  const [info, setInfo] = useState(null);
  const [forms, setForms] = useState({ test: { key_id: "", key_secret: "", webhook: "" }, live: { key_id: "", key_secret: "", webhook: "" } });
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/razorpay-keys"); setInfo(data); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (mode) => {
    const f = forms[mode];
    if (!f.key_id || !f.key_secret) { toast.error("Enter both Key ID and Key Secret"); return; }
    setBusy(mode);
    try {
      await api.post("/admin/razorpay-keys", { mode, key_id: f.key_id.trim(), key_secret: f.key_secret.trim() });
      toast.success(`${mode.toUpperCase()} keys validated & saved (encrypted)`);
      setForms((p) => ({ ...p, [mode]: { key_id: "", key_secret: "" } }));
      await load(); onChange && onChange();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const saveWebhook = async (mode) => {
    const secret = forms[mode].webhook.trim();
    if (!secret) { toast.error("Enter the webhook secret"); return; }
    setBusy(mode + "-wh");
    try {
      await api.post("/admin/razorpay-webhook", { mode, secret });
      toast.success(`${mode.toUpperCase()} webhook secret saved (encrypted)`);
      setForms((p) => ({ ...p, [mode]: { ...p[mode], webhook: "" } }));
      await load(); onChange && onChange();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const revertToEnv = async (mode) => {
    if (!window.confirm(`Remove the stored ${mode.toUpperCase()} keys and fall back to environment keys?`)) return;
    setBusy(mode);
    try {
      await api.delete(`/admin/razorpay-keys/${mode}`);
      toast.success(`${mode.toUpperCase()} keys cleared — using environment keys`);
      await load(); onChange && onChange();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  if (!info) return <div className="mt-3 text-xs text-gray-500">Loading keys…</div>;

  return (
    <div className="mt-4 border-t border-white/10 pt-4 space-y-4" data-testid="razorpay-keys-config">
      {!info.encryption_available && (
        <p className="text-xs text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Encryption not configured (APP_ENCRYPTION_KEY missing) — keys can't be stored from here.</p>
      )}
      <p className="text-[11px] text-gray-500 font-mono">// Keys are validated with a read-only Razorpay check, then encrypted at rest. The secret is never shown back. Empty = falls back to environment keys.</p>
      {["test", "live"].map((mode) => {
        const m = info.modes[mode];
        return (
          <div key={mode} className="border border-white/10 p-3" data-testid={`keys-${mode}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-display text-sm text-white uppercase tracking-wide">{mode} keys</div>
              <div className="flex items-center gap-2">
                {m.configured ? (
                  <span className={`badge ${m.source === "db" ? "badge-accepted" : "badge-upcoming"} flex items-center gap-1`} data-testid={`keys-${mode}-status`}>
                    <Check className="w-3 h-3" /> {m.source === "db" ? "Stored (encrypted)" : "From env"}
                  </span>
                ) : <span className="badge badge-invited" data-testid={`keys-${mode}-status`}>Not set</span>}
                {m.key_id_masked && <span className="text-[11px] font-mono text-gray-400">{m.key_id_masked}</span>}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={forms[mode].key_id} onChange={(e) => setForms((p) => ({ ...p, [mode]: { ...p[mode], key_id: e.target.value } }))}
                placeholder={`${mode === "test" ? "rzp_test_" : "rzp_live_"}…`} className="input-field !py-2 text-sm" data-testid={`keys-${mode}-id`} autoComplete="off" />
              <input type="password" value={forms[mode].key_secret} onChange={(e) => setForms((p) => ({ ...p, [mode]: { ...p[mode], key_secret: e.target.value } }))}
                placeholder="Key Secret" className="input-field !py-2 text-sm" data-testid={`keys-${mode}-secret`} autoComplete="new-password" />
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => save(mode)} disabled={busy === mode || !info.encryption_available} className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`keys-${mode}-save`}>
                {busy === mode ? <span className="spinner" /> : <Save className="w-3.5 h-3.5" />} Validate &amp; Save
              </button>
              {m.source === "db" && (
                <button onClick={() => revertToEnv(mode)} disabled={busy === mode} className="text-[11px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#EF4444]" data-testid={`keys-${mode}-useenv`}>
                  Use env instead
                </button>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] font-mono uppercase tracking-widest text-gray-500">Webhook secret</div>
                {m.webhook_configured
                  ? <span className="badge badge-accepted flex items-center gap-1" data-testid={`wh-${mode}-status`}><Check className="w-3 h-3" /> Set</span>
                  : <span className="badge badge-invited" data-testid={`wh-${mode}-status`}>Not set</span>}
              </div>
              <div className="flex gap-2">
                <input type="password" value={forms[mode].webhook} onChange={(e) => setForms((p) => ({ ...p, [mode]: { ...p[mode], webhook: e.target.value } }))}
                  placeholder="Razorpay webhook signing secret" className="input-field !py-2 text-sm flex-1" data-testid={`wh-${mode}-secret`} autoComplete="new-password" />
                <button onClick={() => saveWebhook(mode)} disabled={busy === mode + "-wh" || !info.encryption_available} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`wh-${mode}-save`}>
                  {busy === mode + "-wh" ? <span className="spinner" /> : <Save className="w-3.5 h-3.5" />} Save
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ===================== Manage Editions (re-status / soft-delete / hard-delete) =====================
function EditionsManagerCard() {
  const [editions, setEditions] = useState([]);
  const [busy, setBusy] = useState("");

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

  const load = async () => {
    try { const { data } = await api.get("/admin/editions"); setEditions(data.editions || []); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restatus = async (ed, status) => {
    setBusy(ed.id);
    try { await api.post(`/admin/editions/${ed.id}/restatus`, { status }); toast.success(`"${ed.name}" set to ${status}`); await load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const toggleSoftDelete = async (ed) => {
    setBusy(ed.id);
    try {
      if (ed.soft_deleted) { await api.post(`/admin/editions/${ed.id}/restore`); toast.success(`"${ed.name}" restored to public`); }
      else { await api.post(`/admin/editions/${ed.id}/soft-delete`); toast.success(`"${ed.name}" hidden from public (kept in admin)`); }
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const hardDelete = async (ed) => {
    const label = `${ed.name} ${ed.edition || ""}`.trim();
    const typed = window.prompt(`⚠️ HARD DELETE is permanent. This wipes ALL data for "${label}" — registrations, teams, submissions, scores, invoices, certificates.\n\nType the edition name to confirm:`, "");
    if (typed === null) return;
    if (typed.trim() !== ed.name.trim()) { toast.error("Name did not match — delete cancelled."); return; }
    setBusy(ed.id);
    try { const { data } = await api.delete(`/admin/editions/${ed.id}`); toast.success(`"${label}" permanently deleted & wiped`); console.log("wiped:", data.wiped); await load(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  if (editions.length <= 1) return null; // nothing to manage when there's only the active edition

  return (
    <div className="card-surface border-l-2 border-white/10" data-testid="editions-manager-panel">
      <div className="flex items-start gap-3 mb-4">
        <Layers className="w-5 h-5 mt-0.5 text-gray-500" strokeWidth={1.5} />
        <div>
          <div className="font-display text-xl text-white">Manage Editions</div>
          <p className="text-xs text-gray-500 mt-1 max-w-xl">Fix a wrongly-archived edition's status, hide it from public pages (soft delete — data stays in admin), or permanently wipe it (hard delete). The <b className="text-gray-300">active</b> edition can't be deleted.</p>
        </div>
      </div>

      <div className="space-y-3">
        {editions.map((ed) => (
          <div key={ed.id} className={`border p-3 rounded-sm ${ed.active ? "border-brand/40" : "border-white/10"}`} data-testid={`edition-manage-${ed.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display text-base text-white flex items-center gap-2 flex-wrap">
                  {ed.name} <span className="font-mono text-xs text-gray-500">{ed.edition || "—"}</span>
                  {ed.active && <span className="badge badge-accepted text-[10px]">ACTIVE</span>}
                  <span className="badge badge-upcoming text-[10px] uppercase">{ed.status}</span>
                  {ed.soft_deleted && <span className="badge badge-rejected text-[10px] uppercase" data-testid={`edition-hidden-${ed.id}`}>hidden</span>}
                </div>
                <div className="text-[11px] font-mono text-gray-500 mt-1">{fmt(ed.r1_open_at)} → {fmt(ed.r2_close_at)}</div>
              </div>
              {!ed.active && (
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) restatus(ed, e.target.value); }}
                    disabled={busy === ed.id}
                    className="input-field !py-1.5 !px-2 text-xs"
                    data-testid={`edition-restatus-${ed.id}`}>
                    <option value="">Set status…</option>
                    {["pre_launch", "upcoming", "r1_open", "r1_review", "r2_open", "judging", "completed"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button onClick={() => toggleSoftDelete(ed)} disabled={busy === ed.id}
                    className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`edition-soft-delete-${ed.id}`}>
                    {ed.soft_deleted ? <><Eye className="w-3.5 h-3.5" /> Restore</> : <><EyeOff className="w-3.5 h-3.5" /> Soft delete</>}
                  </button>
                  <button onClick={() => hardDelete(ed)} disabled={busy === ed.id}
                    className="btn-danger !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`edition-hard-delete-${ed.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Hard delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ===================== Zoho Payments (second gateway + active toggle) =====================
function ZohoGatewayCard() {
  const [info, setInfo] = useState(null);
  const [gw, setGw] = useState(null);
  const [tab, setTab] = useState("test");
  const blank = { account_id: "", region: "IN", client_id: "", client_secret: "", refresh_token: "", api_key: "", webhook: "", code: "" };
  const [forms, setForms] = useState({ test: { ...blank }, live: { ...blank } });
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [k, g] = await Promise.all([api.get("/admin/zoho-keys"), api.get("/admin/active-gateway")]);
      setInfo(k.data);
      setGw(g.data);
      setTab(k.data.active_mode || "test");
      setForms((f) => ({
        test: { ...f.test, account_id: k.data.modes.test.account_id || "", region: k.data.modes.test.region || "IN" },
        live: { ...f.live, account_id: k.data.modes.live.account_id || "", region: k.data.modes.live.region || "IN" },
      }));
    } catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (mode, k, v) => setForms((p) => ({ ...p, [mode]: { ...p[mode], [k]: v } }));

  const copyText = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Couldn't copy — select and copy manually"); }
  };

  const setActiveGateway = async (gateway) => {
    setBusy("gw");
    try {
      const { data } = await api.post("/admin/active-gateway", { gateway });
      toast.success(`Checkout now uses ${data.active_gateway === "zoho" ? "Zoho Payments" : "Razorpay"}`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const setZohoMode = async (mode) => {
    setBusy("mode");
    try {
      const { data } = await api.post("/admin/zoho-mode", { mode });
      toast.success(`Active Zoho environment: ${data.zoho_mode.toUpperCase()}`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const saveKeys = async (mode) => {
    const f = forms[mode];
    setBusy(`keys-${mode}`);
    try {
      await api.post("/admin/zoho-keys", {
        mode, account_id: f.account_id.trim(), region: f.region,
        client_id: f.client_id.trim(), client_secret: f.client_secret.trim(),
        refresh_token: f.refresh_token.trim(), api_key: f.api_key.trim(),
      });
      toast.success(`${mode.toUpperCase()} Zoho credentials saved (encrypted)`);
      setForms((p) => ({ ...p, [mode]: { ...p[mode], client_id: "", client_secret: "", refresh_token: "", api_key: "" } }));
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const saveWebhook = async (mode) => {
    if (!forms[mode].webhook.trim()) { toast.error("Enter the webhook signing key"); return; }
    setBusy(`wh-${mode}`);
    try {
      await api.post("/admin/zoho-webhook", { mode, secret: forms[mode].webhook.trim() });
      toast.success(`${mode.toUpperCase()} webhook signing key saved (encrypted)`);
      setForms((p) => ({ ...p, [mode]: { ...p[mode], webhook: "" } }));
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const generateToken = async (mode) => {
    const f = forms[mode];
    if (!f.client_id.trim() || !f.client_secret.trim() || !f.code.trim()) {
      toast.error("Enter Client ID, Client Secret and the grant code");
      return;
    }
    setBusy(`gen-${mode}`);
    try {
      await api.post("/admin/zoho-generate-token", {
        mode, client_id: f.client_id.trim(), client_secret: f.client_secret.trim(),
        region: f.region, account_id: f.account_id.trim(), code: f.code.trim(),
      });
      toast.success(`Refresh token generated & saved for ${mode.toUpperCase()}`);
      setForms((p) => ({ ...p, [mode]: { ...p[mode], client_id: "", client_secret: "", refresh_token: "", code: "" } }));
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const clearMode = async (mode) => {
    if (!window.confirm(`Remove the ${mode.toUpperCase()} Zoho credentials?`)) return;
    setBusy(`clear-${mode}`);
    try {
      await api.delete(`/admin/zoho-keys/${mode}`);
      toast.success(`${mode.toUpperCase()} Zoho credentials cleared`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  if (!info || !gw) return null;
  const isZohoActive = gw.active_gateway === "zoho";
  const activeModeConfigured = info.modes[info.active_mode]?.configured;

  return (
    <div className={`card-surface border-l-2 ${isZohoActive ? "border-[#E42527]" : "border-white/10"}`} data-testid="zoho-gateway-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <CreditCard className={`w-5 h-5 mt-0.5 ${isZohoActive ? "text-[#E42527]" : "text-gray-500"}`} strokeWidth={1.5} />
          <div>
            <div className="font-display text-xl text-white flex items-center gap-2">
              Zoho Payments
              <span className={`badge ${isZohoActive ? "badge-accepted" : "badge-upcoming"}`} data-testid="zoho-active-state">{isZohoActive ? "ACTIVE GATEWAY" : "INACTIVE"}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-lg">Fallback gateway to Razorpay with separate <b className="text-gray-300">Test</b> and <b className="text-gray-300">Production</b> credentials. All secrets are <b className="text-gray-300">encrypted at rest</b> and never shown back.</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-[11px] font-mono uppercase tracking-widest text-gray-500">Active checkout gateway</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setActiveGateway("razorpay")} disabled={busy === "gw"}
              className={`!py-1.5 !px-3 text-xs ${!isZohoActive ? "btn-primary" : "btn-secondary"}`} data-testid="gateway-razorpay-btn">Razorpay</button>
            <button onClick={() => setActiveGateway("zoho")} disabled={busy === "gw" || !activeModeConfigured}
              className={`!py-1.5 !px-3 text-xs ${isZohoActive ? "btn-primary" : "btn-secondary"}`} data-testid="gateway-zoho-btn">Zoho</button>
          </div>
          {!activeModeConfigured && <span className="text-[11px] text-gray-500">Configure the active {info.active_mode.toUpperCase()} env first</span>}
        </div>
      </div>

      <div className="mt-4 border-t border-white/10 pt-4 space-y-3" data-testid="zoho-keys-config">
        {!info.encryption_available && (
          <p className="text-xs text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Encryption not configured (APP_ENCRYPTION_KEY missing) — keys can't be stored.</p>
        )}

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono uppercase tracking-widest text-gray-500">Active Zoho env:</span>
            <button onClick={() => setZohoMode("test")} disabled={busy === "mode"}
              className={`!py-1 !px-3 text-xs ${info.active_mode === "test" ? "btn-primary" : "btn-secondary"}`} data-testid="zoho-mode-test">Test</button>
            <button onClick={() => setZohoMode("live")} disabled={busy === "mode" || !info.modes.live.configured}
              className={`!py-1 !px-3 text-xs ${info.active_mode === "live" ? "btn-primary" : "btn-secondary"}`} data-testid="zoho-mode-live">Production</button>
          </div>
          <div className="flex items-center gap-1 border border-white/10 rounded-sm p-0.5">
            {["test", "live"].map((m) => (
              <button key={m} onClick={() => setTab(m)}
                className={`!py-1 !px-3 text-xs rounded-sm ${tab === m ? "bg-white/10 text-white" : "text-gray-400"}`} data-testid={`zoho-tab-${m}`}>
                {m === "test" ? "Test" : "Production"} {info.modes[m].configured && <Check className="w-3 h-3 inline text-emerald-400" />}
              </button>
            ))}
          </div>
        </div>

        {["test", "live"].map((mode) => {
          if (tab !== mode) return null;
          const f = forms[mode];
          const m = info.modes[mode];
          return (
            <div key={mode} className={`border p-3 rounded-sm ${info.active_mode === mode ? "border-[#E42527]/40" : "border-white/10"}`} data-testid={`zoho-mode-block-${mode}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display text-sm text-white uppercase tracking-wide flex items-center gap-2">
                  {mode === "test" ? "Test" : "Production"} credentials
                  {info.active_mode === mode && <span className="badge badge-accepted text-[10px]">ACTIVE</span>}
                </div>
                <span className={`badge ${m.configured ? "badge-accepted" : "badge-invited"}`} data-testid={`zoho-${mode}-configured`}>{m.configured ? "Configured" : "Incomplete"}</span>
              </div>
              <p className="text-[11px] text-gray-500 font-mono mb-3">// Only non-empty fields are saved — rotate one secret without re-entering the rest.</p>

              <div className="border border-[#5B8DEF]/40 bg-[#5B8DEF]/5 p-3 rounded-sm mb-3" data-testid={`zoho-${mode}-gen-box`}>
                <div className="font-display text-xs text-white uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-[#5B8DEF]" /> Generate refresh token (no curl needed)
                </div>
                <p className="text-[11px] text-gray-500 leading-snug mb-2">
                  In Zoho API Console → <b className="text-gray-300">Self Client → Generate Code</b>. Paste the scope below (copy it), fill <b className="text-gray-300">Client ID + Secret</b>, then paste the <b className="text-gray-300">grant code</b> and hit Generate — it exchanges + saves the refresh token automatically. <span className="text-amber-400">The code expires in minutes, so do this right after generating it.</span>
                </p>
                <label className="label-mono block mb-1">Scope (paste into Zoho's "Scope" field)</label>
                <div className="flex gap-2 mb-2">
                  <input readOnly value="ZohoPay.payments.CREATE,ZohoPay.payments.READ,ZohoPay.settings.READ"
                    className="input-field !py-2 text-sm flex-1 font-mono" data-testid={`zoho-${mode}-scope`} onFocus={(e) => e.target.select()} />
                  <button onClick={() => copyText("ZohoPay.payments.CREATE,ZohoPay.payments.READ,ZohoPay.settings.READ", "Scope")}
                    className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`zoho-${mode}-scope-copy`}>
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
                <input value={f.code} onChange={(e) => setField(mode, "code", e.target.value)}
                  placeholder="Paste grant code (e.g. 1000.abc123...def456)" className="input-field !py-2 text-sm w-full mb-2 font-mono" data-testid={`zoho-${mode}-grant-code`} autoComplete="off" />
                <button onClick={() => generateToken(mode)} disabled={busy === `gen-${mode}` || !info.encryption_available}
                  className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`zoho-${mode}-generate-token`}>
                  {busy === `gen-${mode}` ? <span className="spinner" /> : <RefreshCw className="w-3.5 h-3.5" />} Generate &amp; Save Refresh Token
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1.5">Account ID</label>
                  <input value={f.account_id} onChange={(e) => setField(mode, "account_id", e.target.value)}
                    placeholder="e.g. 60062045905" className="input-field !py-2 text-sm" data-testid={`zoho-${mode}-account-id`} autoComplete="off" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Data Center / Region</label>
                  <select value={f.region} onChange={(e) => setField(mode, "region", e.target.value)} className="input-field !py-2 text-sm" data-testid={`zoho-${mode}-region`}>
                    <option value="IN">India (zoho.in)</option>
                    <option value="US">US / Global (zoho.com)</option>
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Client ID {m.client_id_set && <span className="text-emerald-400 normal-case">· set</span>}</label>
                  <input value={f.client_id} onChange={(e) => setField(mode, "client_id", e.target.value)}
                    placeholder="1000.XXXXXXXX" className="input-field !py-2 text-sm" data-testid={`zoho-${mode}-client-id`} autoComplete="off" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Client Secret {m.client_secret_set && <span className="text-emerald-400 normal-case">· set</span>}</label>
                  <input type="password" value={f.client_secret} onChange={(e) => setField(mode, "client_secret", e.target.value)}
                    placeholder="••••••••" className="input-field !py-2 text-sm" data-testid={`zoho-${mode}-client-secret`} autoComplete="new-password" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Refresh Token {m.refresh_token_set && <span className="text-emerald-400 normal-case">· set</span>}</label>
                  <input type="password" value={f.refresh_token} onChange={(e) => setField(mode, "refresh_token", e.target.value)}
                    placeholder="1000.XXXX.YYYY" className="input-field !py-2 text-sm" data-testid={`zoho-${mode}-refresh-token`} autoComplete="new-password" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Widget API Key {m.api_key_set && <span className="text-emerald-400 normal-case">· set</span>}</label>
                  <input value={f.api_key} onChange={(e) => setField(mode, "api_key", e.target.value)}
                    placeholder="Public widget api_key" className="input-field !py-2 text-sm" data-testid={`zoho-${mode}-api-key`} autoComplete="off" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button onClick={() => saveKeys(mode)} disabled={busy === `keys-${mode}` || !info.encryption_available}
                  className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`zoho-${mode}-save-keys`}>
                  {busy === `keys-${mode}` ? <span className="spinner" /> : <Save className="w-3.5 h-3.5" />} Save {mode === "test" ? "Test" : "Production"} Credentials
                </button>
                {(m.client_id_set || m.account_id) && (
                  <button onClick={() => clearMode(mode)} disabled={busy === `clear-${mode}`} className="text-[11px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#EF4444]" data-testid={`zoho-${mode}-clear`}>
                    Clear {mode}
                  </button>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-white/5">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-gray-500">Webhook signing key</div>
                  {m.webhook_configured
                    ? <span className="badge badge-accepted flex items-center gap-1" data-testid={`zoho-${mode}-wh-status`}><Check className="w-3 h-3" /> Set</span>
                    : <span className="badge badge-invited" data-testid={`zoho-${mode}-wh-status`}>Not set</span>}
                </div>
                <div className="flex gap-2">
                  <input type="password" value={f.webhook} onChange={(e) => setField(mode, "webhook", e.target.value)}
                    placeholder="Zoho webhook signing key" className="input-field !py-2 text-sm flex-1" data-testid={`zoho-${mode}-webhook-secret`} autoComplete="new-password" />
                  <button onClick={() => saveWebhook(mode)} disabled={busy === `wh-${mode}` || !info.encryption_available} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`zoho-${mode}-save-webhook`}>
                    {busy === `wh-${mode}` ? <span className="spinner" /> : <Save className="w-3.5 h-3.5" />} Save
                  </button>
                </div>
                <p className="text-[11px] text-gray-600 mt-1.5 font-mono break-all">Webhook URL: {window.location.origin}/api/payment/zoho/webhook · events: payment.succeeded, payment.failed</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ===================== NoobSync Ambassador referral integration =====================
function AmbassadorCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [urls, setUrls] = useState({ test: "", prod: "" });
  const [keys, setKeys] = useState({ test: "", prod: "" });
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ambassador-config");
      setInfo(data);
      setUrls({ test: data.modes.test.url || "", prod: data.modes.prod.url || "" });
    } catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveConfig = async (patch) => {
    setBusy("config");
    try {
      await api.post("/admin/ambassador-config", patch);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const saveUrl = async (mode) => {
    setBusy(`url-${mode}`);
    try {
      await api.post("/admin/ambassador-config", { [`${mode}_url`]: urls[mode].trim() });
      toast.success(`${mode.toUpperCase()} URL saved`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const saveKey = async (mode) => {
    const k = keys[mode].trim();
    if (!k) { toast.error("Enter the API key"); return; }
    setBusy(`key-${mode}`);
    try {
      await api.post("/admin/ambassador-key", { mode, api_key: k });
      toast.success(`${mode.toUpperCase()} API key validated & saved (encrypted)`);
      setKeys((p) => ({ ...p, [mode]: "" }));
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const clearKey = async (mode) => {
    if (!window.confirm(`Clear the stored ${mode.toUpperCase()} API key?`)) return;
    setBusy(`key-${mode}`);
    try {
      await api.post("/admin/ambassador-key", { mode, api_key: "" });
      toast.success(`${mode.toUpperCase()} key cleared`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/ambassador-test");
      setTestResult(data);
      if (data.ok) toast.success("Test event delivered — wiring looks good");
      else toast.error("Test event failed — check the result below");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  if (!info) return null;
  const isTest = info.mode === "test";
  const ready = info.effective?.ready;

  return (
    <div className={`card-surface border-l-2 ${!info.enabled ? "border-gray-600" : isTest ? "border-[#F59E0B]" : "border-emerald-500"}`} data-testid="ambassador-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Gift className={`w-5 h-5 mt-0.5 ${!info.enabled ? "text-gray-500" : isTest ? "text-[#F59E0B]" : "text-emerald-400"}`} strokeWidth={1.5} />
          <div>
            <div className="font-display text-xl text-white flex items-center gap-2">
              Ambassador Referrals
              <span className={`badge ${!info.enabled ? "badge-upcoming" : isTest ? "badge-invited" : "badge-accepted"}`} data-testid="ambassador-mode-state">
                {!info.enabled ? "OFF" : isTest ? "TEST" : "PROD"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-lg">
              NoobSync Brand Ambassador attribution. The active environment decides which URL + key are used. Keys are <b className="text-gray-300">encrypted at rest</b> and never shown back.
            </p>
            {info.enabled && !ready && (
              <p className="text-[11px] text-amber-400 mt-2 flex items-center gap-1.5" data-testid="ambassador-not-ready">
                <AlertTriangle className="w-3.5 h-3.5" /> {isTest ? "TEST" : "PROD"} URL or key missing — referrals won't be sent until both are set.
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <button onClick={() => saveConfig({ mode: "test" })} disabled={busy === "config"}
              className={`!py-1.5 !px-3 text-xs ${isTest ? "btn-primary" : "btn-secondary"}`} data-testid="ambassador-mode-test">Test</button>
            <button onClick={() => saveConfig({ mode: "prod" })} disabled={busy === "config"}
              className={`!py-1.5 !px-3 text-xs ${!isTest ? "btn-primary" : "btn-secondary"}`} data-testid="ambassador-mode-prod">Prod</button>
          </div>
          <button onClick={() => saveConfig({ enabled: !info.enabled })} disabled={busy === "config"}
            className="text-[11px] font-mono uppercase tracking-widest text-gray-400 hover:text-brand" data-testid="ambassador-toggle-enabled">
            {info.enabled ? "Disable integration" : "Enable integration"}
          </button>
        </div>
      </div>

      <div className="mt-4 border-t border-white/10 pt-4 space-y-4" data-testid="ambassador-config">
        {!info.encryption_available && (
          <p className="text-xs text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Encryption not configured (APP_ENCRYPTION_KEY missing) — keys can't be stored from here.</p>
        )}
        {["test", "prod"].map((mode) => {
          const m = info.modes[mode];
          return (
            <div key={mode} className={`border p-3 ${info.mode === mode ? "border-brand/50" : "border-white/10"}`} data-testid={`ambassador-${mode}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display text-sm text-white uppercase tracking-wide flex items-center gap-2">
                  {mode}
                  {info.mode === mode && <span className="badge badge-accepted text-[10px]">ACTIVE</span>}
                </div>
                {m.key_configured
                  ? <span className="badge badge-accepted flex items-center gap-1" data-testid={`ambassador-${mode}-key-status`}><Check className="w-3 h-3" /> Key set {m.key_masked && <span className="font-mono text-gray-300 ml-1">{m.key_masked}</span>}</span>
                  : <span className="badge badge-invited" data-testid={`ambassador-${mode}-key-status`}>No key</span>}
              </div>
              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <Globe className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input value={urls[mode]} onChange={(e) => setUrls((p) => ({ ...p, [mode]: e.target.value }))}
                    placeholder="https://…ambassador base URL" className="input-field !py-2 !pl-8 text-sm" data-testid={`ambassador-${mode}-url`} autoComplete="off" />
                </div>
                <button onClick={() => saveUrl(mode)} disabled={busy === `url-${mode}`} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`ambassador-${mode}-url-save`}>
                  {busy === `url-${mode}` ? <span className="spinner" /> : <Save className="w-3.5 h-3.5" />} URL
                </button>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <KeyRound className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input type="password" value={keys[mode]} onChange={(e) => setKeys((p) => ({ ...p, [mode]: e.target.value }))}
                    placeholder="X-API-Key (from ambassador Integrations)" className="input-field !py-2 !pl-8 text-sm" data-testid={`ambassador-${mode}-key`} autoComplete="new-password" />
                </div>
                <button onClick={() => saveKey(mode)} disabled={busy === `key-${mode}` || !info.encryption_available} className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`ambassador-${mode}-key-save`}>
                  {busy === `key-${mode}` ? <span className="spinner" /> : <Save className="w-3.5 h-3.5" />} Key
                </button>
                {m.key_configured && (
                  <button onClick={() => clearKey(mode)} disabled={busy === `key-${mode}`} className="text-[11px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#EF4444]" data-testid={`ambassador-${mode}-key-clear`}>Clear</button>
                )}
              </div>
            </div>
          );
        })}
        <div className="flex items-center gap-3 flex-wrap pt-1">
          <button onClick={sendTest} disabled={busy === "test" || !info.effective?.ready}
            className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid="ambassador-send-test">
            {busy === "test" ? <span className="spinner" /> : <Sparkles className="w-3.5 h-3.5" />} Send test event ({info.mode})
          </button>
          {!info.effective?.ready && <span className="text-[11px] text-gray-500">Configure the active mode's URL + key first.</span>}
        </div>
        {testResult && (
          <div className={`border-l-2 p-3 text-xs ${testResult.ok ? "border-emerald-500 bg-emerald-500/5" : "border-[#EF4444] bg-[#EF4444]/5"}`} data-testid="ambassador-test-result">
            <div className="flex items-center gap-2 mb-2">
              {testResult.ok ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-[#EF4444]" />}
              <span className={`font-display ${testResult.ok ? "text-emerald-400" : "text-[#EF4444]"}`}>
                {testResult.ok ? "All probes passed" : "Probe(s) failed"} · {testResult.mode?.toUpperCase()}
              </span>
            </div>
            {testResult.probes ? (
              <div className="space-y-1.5">
                {[["track", "Signup (/track)"], ["convert", "Paid (/convert)"]].map(([k, label]) => {
                  const p = testResult.probes[k];
                  if (!p) return null;
                  return (
                    <div key={k} className="flex items-start gap-2" data-testid={`ambassador-probe-${k}`}>
                      {p.ok ? <Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3 h-3 text-[#EF4444] mt-0.5 shrink-0" />}
                      <div className="min-w-0">
                        <span className="text-gray-300">{label}</span>
                        {p.status_code ? <span className="text-gray-500"> · HTTP {p.status_code}</span> : ""}
                        <p className="text-gray-500 font-mono break-all">{p.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 font-mono break-all">{testResult.detail}</p>
            )}
            {testResult.ok && (
              <p className="text-gray-500 mt-2">404 “not found” is expected — it confirms the URL + key are valid (we send dummy data).</p>
            )}
          </div>
        )}
        <p className="text-[11px] text-gray-500 font-mono">// The key is validated against the mode's URL (non-destructive probe) before saving, then encrypted. Empty key = falls back to environment variables.</p>
        <VerifyKeyPanel encryptionAvailable={info.encryption_available} />

      </div>
    </div>
  );
}


// ===================== IdeaThon payment-verification key (outbound, for the Ambassador platform) =====================
function VerifyKeyPanel({ encryptionAvailable }) {
  const [info, setInfo] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/verify-key"); setInfo(data); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Couldn't copy — select and copy manually"); }
  };

  const rotate = async () => {
    if (!window.confirm("Generate a NEW verification key? The current key stops working immediately — you must paste the new AUTH HEADER into the Ambassador platform right after, or referral conversions will fail to verify.")) return;
    setBusy("rotate");
    try {
      await api.post("/admin/verify-key/rotate");
      toast.success("New key generated. Update the Ambassador platform's AUTH HEADER now.");
      setReveal(true);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  if (!info) return null;
  const masked = info.api_key ? `${info.api_key.slice(0, 11)}${"•".repeat(12)}${info.api_key.slice(-4)}` : "";

  return (
    <div className="border border-[#5B8DEF]/40 p-3 rounded-sm" data-testid="verify-key-panel">
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-display text-sm text-white uppercase tracking-wide flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#5B8DEF]" /> Payment verification (paste into Ambassador)
        </div>
        <span className={`badge ${info.configured ? "badge-accepted" : "badge-invited"} flex items-center gap-1`} data-testid="verify-key-source">
          {info.configured ? <><Check className="w-3 h-3" /> {info.source === "db" ? "Rotated key" : "From env"}</> : "Not set"}
        </span>
      </div>
      <p className="text-[11px] text-gray-500 mb-3 leading-snug">
        The Ambassador platform calls this URL with the <span className="font-mono text-gray-300">payment_reference</span> we send on each conversion, and only awards points if it confirms the payment. Paste both values into the Ambassador platform's <b className="text-gray-300">"Payment verification for IdeaThon"</b> section.
      </p>

      <label className="label-mono block mb-1">Verify URL</label>
      <div className="flex gap-2 mb-3">
        <input readOnly value={info.verify_url} className="input-field !py-2 text-sm flex-1 font-mono" data-testid="verify-url" onFocus={(e) => e.target.select()} />
        <button onClick={() => copy(info.verify_url, "Verify URL")} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid="verify-url-copy">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>

      <label className="label-mono block mb-1">Auth Header</label>
      <div className="flex gap-2">
        <input readOnly value={info.api_key ? (reveal ? info.auth_header : `X-API-Key: ${masked}`) : "Not configured"}
          className="input-field !py-2 text-sm flex-1 font-mono" data-testid="verify-auth-header" onFocus={(e) => reveal && e.target.select()} />
        <button onClick={() => setReveal((s) => !s)} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid="verify-key-reveal" title={reveal ? "Hide" : "Reveal"}>
          {reveal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => copy(info.auth_header, "Auth header")} disabled={!info.api_key} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid="verify-key-copy">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button onClick={rotate} disabled={busy === "rotate" || !encryptionAvailable}
          className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid="verify-key-rotate">
          {busy === "rotate" ? <span className="spinner" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerate key
        </button>
        {info.rotated_at && <span className="text-[11px] text-gray-500">Last rotated: {new Date(info.rotated_at).toLocaleString()}</span>}
      </div>
      {!encryptionAvailable && (
        <p className="text-[11px] text-amber-400 mt-2 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Encryption not configured — rotation is disabled. The env key is still used.</p>
      )}
    </div>
  );
}


function MetaPixelCard() {
  const [cfg, setCfg] = useState(null);
  const [pixelId, setPixelId] = useState("");
  const [token, setToken] = useState("");
  const [testCode, setTestCode] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/meta-config");
      setCfg(data);
      setPixelId(data.pixel_id || "");
      setTestCode(data.test_event_code || "");
    } catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (extra = {}) => {
    setBusy("save");
    try {
      const payload = { pixel_id: pixelId.trim(), test_event_code: testCode.trim(), ...extra };
      if (token.trim()) payload.capi_token = token.trim();
      await api.post("/admin/meta-config", payload);
      toast.success("Meta settings saved (token encrypted at rest)");
      setToken("");
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  const clearToken = async () => {
    if (!window.confirm("Remove the stored Conversions API token? Server-side tracking will stop.")) return;
    setBusy("clear");
    try {
      await api.post("/admin/meta-config/clear-token");
      toast.success("CAPI token cleared");
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(""); }
  };

  if (!cfg) return null;
  const tokenSet = cfg.capi_token_set;

  return (
    <div className={`card-surface border-l-2 ${cfg.tracking_enabled && tokenSet ? "border-emerald-500" : "border-gray-600"}`} data-testid="meta-pixel-panel">
      <div className="flex items-start gap-3">
        <BarChart3 className={`w-5 h-5 mt-0.5 ${cfg.tracking_enabled && tokenSet ? "text-emerald-400" : "text-gray-500"}`} strokeWidth={1.5} />
        <div className="flex-1">
          <div className="font-display text-xl text-white flex items-center gap-2">
            Meta Pixel &amp; Conversions API
            <span className={`badge ${tokenSet ? "badge-accepted" : "badge-upcoming"}`} data-testid="meta-capi-state">
              {tokenSet ? `CAPI ${cfg.capi_token_source === "db" ? "SET" : "ENV"}` : "PIXEL ONLY"}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1 max-w-lg">
            Server-side conversion tracking. The access token is <b className="text-gray-300">encrypted at rest</b> (Fernet) and never shown back. Add it here from Meta Events Manager → your Pixel → Conversions API → Generate access token.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4 max-w-lg">
        <div>
          <label className="label-mono block mb-1.5">Pixel / Dataset ID</label>
          <input value={pixelId} onChange={(e) => setPixelId(e.target.value)} placeholder="1390755592903052" className="input-field" data-testid="meta-pixel-id-input" />
        </div>

        <div>
          <label className="label-mono block mb-1.5">Conversions API Access Token {tokenSet && <span className="text-emerald-400">· configured</span>}</label>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
            placeholder={tokenSet ? "•••••••• (leave blank to keep current)" : "EAAB… paste token to enable server-side tracking"}
            className="input-field" data-testid="meta-capi-token-input" autoComplete="off" />
          {!cfg.encryption_available && (
            <p className="text-[11px] text-amber-400 mt-1.5 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Encryption not configured (APP_ENCRYPTION_KEY missing) — cannot store the token securely.</p>
          )}
        </div>

        <div>
          <label className="label-mono block mb-1.5">Test Event Code <span className="text-gray-600">(optional — Events Manager → Test Events)</span></label>
          <input value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="TEST12345" className="input-field" data-testid="meta-test-code-input" />
        </div>

        <label className="flex items-center gap-3">
          <input type="checkbox" checked={!!cfg.tracking_enabled} onChange={(e) => save({ tracking_enabled: e.target.checked })} className="accent-[#FF6B2B]" data-testid="meta-tracking-toggle" />
          <span className="text-sm text-gray-300">Meta tracking enabled (master switch for Pixel + CAPI)</span>
        </label>

        <div className="flex gap-2">
          <button onClick={() => save()} disabled={busy === "save"} className="btn-primary flex items-center justify-center gap-2" data-testid="meta-save-btn">
            {busy === "save" ? <span className="spinner" /> : <Save className="w-4 h-4" />} Save
          </button>
          {cfg.capi_token_source === "db" && (
            <button onClick={clearToken} disabled={busy === "clear"} className="btn-secondary flex items-center justify-center gap-2" data-testid="meta-clear-token-btn">
              <Trash2 className="w-4 h-4" /> Clear token
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
